"use client";

import { jwtDecode } from "jwt-decode";
import { useState, useEffect, useRef } from "react";
import {
  Camera,
  ImageIcon,
  MapPin,
  Link2,
  ChevronDown,
  Hash,
  Check,
  X,
  Plus,
  Search,
  Loader2,
} from "lucide-react";
import Button from "./Button";
import Popover from "./Popover";
import { classNames } from "../../utils/classNames";
import ImagePickerModal from "./ImagePickerModal";
import { successToast, errorToast, warningToast } from "../../utils/toasters";
import { postScoops, getScoops, getHoops } from "../../services/scoops";
import { handlegetAllGroup, handleAddGroupPost } from "../../services/group";
import {
  base64ToFiles,
  filesToBase64,
  isAuthenticated,
} from "../../utils/helpers";
import { useNavigate, Link } from "react-router-dom";
import SocialPostCard from "../home_components/SocialPostCard";

// Import skeleton components
import PostSkeleton from "../skeletons/PostSkeleton";

function Hashtag({ tag, onClick, selected }) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        "px-3 py-1 rounded-full text-sm font-medium",
        selected
          ? "bg-black text-white"
          : "border text-black hover:bg-gray-800 hover:text-white"
      )}
    >
      {tag}
    </button>
  );
}

function CreateScoopForm({
  followingUsers = [],
  postings = [],
  setPostings = null,
  feedLoading = false,
  initialLoading = false,
  error = null,
  hasMore = true,
  lastPostElementRef = () => {},
  refreshFeed = () => {},
}) {
  const [content, setContent] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedHashtags, setSelectedHashtags] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [images, setImages] = useState([]); // Store multiple image files
  const [imagePickerMode, setImagePickerMode] = useState("upload");
  const [isHashtagPopoverOpen, setIsHashtagPopoverOpen] = useState(false);
  const [isCategoryPopoverOpen, setIsCategoryPopoverOpen] = useState(false);
  const [isGroupPopoverOpen, setIsGroupPopoverOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [geohash, setGeohash] = useState("9v6m"); // Default geohash
  const navigate = useNavigate();

  // Refs for popover content elements
  const groupPopoverRef = useRef(null);
  const categoryPopoverRef = useRef(null);
  const hashtagPopoverRef = useRef(null);

  const groupTriggerRef = useRef(null);
  const categoryTriggerRef = useRef(null);
  const hashtagTriggerRef = useRef(null);
  // Autocomplete/mention feature states
  const textareaRef = useRef(null);
  const mentionGuideRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionType, setMentionType] = useState(null); // '@', '#', or '+'
  const [mentionText, setMentionText] = useState("");
  const [mentionOptions, setMentionOptions] = useState([]);
  const [showMentionGuide, setShowMentionGuide] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  // State for API data
  const [scoops, setScoops] = useState([]);
  const [hoops, setHoops] = useState([]);
  const [groups, setGroups] = useState([]);
  const [availableHoops, setAvailableHoops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultGallyHoop, setDefaultGallyHoop] = useState(null);

  // Loading states for different sections
  const [formDataLoading, setFormDataLoading] = useState(true);

  // Check if user is logged in
  const userIsLoggedIn = isAuthenticated();

  // Render skeleton loaders for posts
  const renderPostSkeletons = (count = 3) => {
    return Array.from({ length: count }, (_, index) => (
      <PostSkeleton key={`post-skeleton-${index}`} />
    ));
  };

  // Fetch scoops and hoops when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setFormDataLoading(true);

        // Fetch scoops and hoops in parallel
        const [scoopsData, hoopsData] = await Promise.all([
          getScoops(),
          getHoops(),
        ]);

        setScoops(scoopsData);
        setHoops(hoopsData);

        // Group hoops for display
        const groupedHoops = groupHoopsByCategory(hoopsData);
        setAvailableHoops(groupedHoops);

        // Find Gally Talks scoop and set default hoop
        const gallyTalksScoop = scoopsData.find(
          (scoop) =>
            scoop.scoopname === "Gally Talks" ||
            scoop.scoopname === "Galli Talks"
        );

        if (
          gallyTalksScoop &&
          gallyTalksScoop.hoops &&
          gallyTalksScoop.hoops.length > 0
        ) {
          // Find the first hoop for Gally Talks and set as default
          const firstHoopId = gallyTalksScoop.hoops[0].hoopid;
          const defaultHoop = hoopsData.find(
            (hoop) => hoop.hoopid === firstHoopId
          );
          if (defaultHoop) {
            setDefaultGallyHoop(defaultHoop);
          }
        }
      } catch (error) {
        errorToast("Failed to load categories and hashtags: " + error.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      } finally {
        setIsLoading(false);
        setFormDataLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch groups when component mounts or when the groups button is clicked
  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const geoHash = "9v6m";
      const token = localStorage.getItem("access_token");
      let userId = null;
      if (token) {
        const decoded = jwtDecode(token);
        userId = decoded?.userid || decoded?.id || null;
      }
      const groupsData = await handlegetAllGroup({ geoHash: geohash, userId });

      // Filter to only include groups where isJoined is true
      const joinedGroups = (groupsData.data.groups || []).filter(
        (group) => group.isJoined === true
      );

      setGroups(joinedGroups);
      return joinedGroups;
    } catch (error) {
      console.error("Failed to load groups:", error);
      errorToast("Failed to load groups: " + error.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch groups when component mounts
  useEffect(() => {
    if (userIsLoggedIn) {
      fetchGroups();
    }
  }, [userIsLoggedIn]);

  // Group hoops into arrays for display
  const groupHoopsByCategory = (hoopsData) => {
    // Create groups of 6 hoops each
    const groups = [];
    for (let i = 0; i < hoopsData.length; i += 6) {
      groups.push(hoopsData.slice(i, i + 6));
    }
    return groups;
  };

  // Get geolocation when component mounts
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // In a real app, you would convert lat/long to geohash
          // For now, we'll use a default value
          setGeohash("9v6m");
        },
        (error) => {
          console.error("Error getting geolocation:", error);
        }
      );
    }
  }, []);

  // Update available hoops when selected category changes
  useEffect(() => {
    if (selectedCategory && scoops.length > 0) {
      // Find the selected scoop
      const scoop = scoops.find(
        (s) => s.scoopid.toString() === selectedCategory.id
      );

      if (scoop && scoop.hoops) {
        // Group the hoops for this scoop
        const scoopHoops = scoop.hoops.map((h) => h.hoopid);

        // Filter all hoops to only include those for this scoop
        const filteredHoops = hoops.filter((h) =>
          scoopHoops.includes(h.hoopid)
        );

        // Group the filtered hoops
        const groupedHoops = groupHoopsByCategory(filteredHoops);
        setAvailableHoops(groupedHoops);
      }
    }
  }, [selectedCategory, scoops, hoops]);

  // Set default scoop to "Gally Talks" when a group is selected
  useEffect(() => {
    if (selectedGroups.length > 0) {
      // Find the Gally Talks scoop
      const gallyTalksScoop = scoops.find(
        (scoop) =>
          scoop.scoopname === "Gally Talks" || scoop.scoopname === "Galli Talks"
      );

      if (gallyTalksScoop) {
        setSelectedCategory({
          id: gallyTalksScoop.scoopid.toString(),
          name: gallyTalksScoop.scoopname,
          iconPath: `/scoops/${gallyTalksScoop.scoopname
            .toLowerCase()
            .replace(/\s+/g, "")}.png`,
        });

        // Fetch hoops for this scoop
        const scoopHoops = gallyTalksScoop.hoops || [];
        const filteredHoops = hoops.filter((h) =>
          scoopHoops.some((sh) => sh.hoopid === h.hoopid)
        );
        const groupedHoops = groupHoopsByCategory(filteredHoops);
        setAvailableHoops(groupedHoops);

        // Set default hoop if it exists and no hoops are currently selected
        if (defaultGallyHoop && selectedHashtags.length === 0) {
          setSelectedHashtags([defaultGallyHoop]);
        }
      }
    }
  }, [selectedGroups, scoops, hoops, defaultGallyHoop]);

  // Handle text input and detect mentions
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);

    // Get cursor position
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);

    // Check if we're in the middle of typing a mention
    const textBeforeCursor = newContent.substring(0, cursorPos);
    const words = textBeforeCursor.split(/\s/);
    const currentWord = words[words.length - 1];

    // Always show the dropdown if we're typing a mention character
    if (currentWord.startsWith("+") && userIsLoggedIn) {
      // Group mention - only show if user is logged in
      const searchText = currentWord.substring(1).toLowerCase();
      setMentionType("+");
      setMentionText(searchText);
      setSelectedMentionIndex(0);
      setShowMentionGuide(true);
      calculateMentionPosition();

      // First, fetch the groups and then update the mention options
      fetchGroups().then((fetchedGroups) => {
        // Use the returned groups from the fetchGroups function
        const groupsToFilter = fetchedGroups || groups;

        // Filter groups based on search text
        const filteredGroups = groupsToFilter.filter((group) => {
          return group.name.toLowerCase().includes(searchText);
        });

        setMentionOptions(
          filteredGroups.map((group) => ({
            id: group.groupid.toString(),
            name: group.name,
            type: "group",
          }))
        );
      });
    } else if (currentWord.startsWith("@")) {
      // Scoop mention - only show if a group is selected or no group is required
      const searchText = currentWord.substring(1).toLowerCase();
      setMentionType("@");
      setMentionText(searchText);
      setSelectedMentionIndex(0);

      // Ensure we have the latest scoops data
      getScoops().then((scoopsData) => {
        // Update the scoops state with fresh data
        setScoops(scoopsData);

        // Filter scoops based on search text
        let filteredScoops;
        if (selectedGroups.length > 0) {
          // Only show Gally Talks scoop
          filteredScoops = scoopsData
            .filter(
              (scoop) =>
                (scoop.scoopname === "Gally Talks" ||
                  scoop.scoopname === "Galli Talks") &&
                scoop.scoopname.toLowerCase().includes(searchText)
            )
            .slice(0, 5);
        } else {
          // Show all scoops
          filteredScoops = scoopsData.filter((scoop) => {
            return scoop.scoopname.toLowerCase().includes(searchText);
          });
        }

        setMentionOptions(
          filteredScoops.map((scoop) => ({
            id: scoop.scoopid.toString(),
            name: scoop.scoopname,
            type: "scoop",
            iconPath: `/scoops/${scoop.scoopname
              .toLowerCase()
              .replace(/\s+/g, "")}.png`,
          }))
        );
      });

      setShowMentionGuide(true);
      calculateMentionPosition();
    } else if (currentWord.startsWith("#")) {
      // Hoop mention - only show if a scoop is selected
      if (!selectedCategory) {
        // Don't show dropdown if no scoop is selected
        setShowMentionGuide(false);
        return;
      }

      const searchText = currentWord.substring(1).toLowerCase();
      setMentionType("#");
      setMentionText(searchText);
      setSelectedMentionIndex(0);

      // Filter hoops based on selected scoop
      const filteredHoops = hoops
        .filter((hoop) => {
          const scoop = scoops.find(
            (s) => s.scoopid.toString() === selectedCategory.id
          );
          if (!scoop || !scoop.hoops) return false;

          const scoopHoops = scoop.hoops.map((h) => h.hoopid);
          return (
            scoopHoops.includes(hoop.hoopid) &&
            hoop.hooptag.toLowerCase().includes(searchText)
          );
        })
        .slice(0, 5);

      setMentionOptions(
        filteredHoops.map((hoop) => ({
          id: hoop.hoopid.toString(),
          name: hoop.hooptag,
          type: "hoop",
        }))
      );

      setShowMentionGuide(true);
      calculateMentionPosition();
    } else {
      // Not typing a mention
      setShowMentionGuide(false);
    }
  };

  // Calculate position for mention guide - directly below cursor
  const calculateMentionPosition = () => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const textareaRect = textarea.getBoundingClientRect();

    // Get the container dimensions for boundary checking
    const containerEl = textarea.parentElement;
    const containerRect = containerEl.getBoundingClientRect();

    // Get cursor position
    const cursorPosition = textarea.selectionEnd;

    // Create a temporary element to measure text dimensions
    const tempElement = document.createElement("div");
    tempElement.style.position = "absolute";
    tempElement.style.visibility = "hidden";
    tempElement.style.whiteSpace = "pre-wrap";
    tempElement.style.width = `${textareaRect.width}px`;

    // Copy all relevant styles from the textarea
    const textareaStyles = window.getComputedStyle(textarea);
    const stylesToCopy = [
      "font",
      "fontSize",
      "fontFamily",
      "lineHeight",
      "letterSpacing",
      "textTransform",
      "padding",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "boxSizing",
      "borderWidth",
    ];

    stylesToCopy.forEach((style) => {
      tempElement.style[style] = textareaStyles[style];
    });

    // Get text before cursor and add a span at cursor position
    const textBeforeCursor = textarea.value.substring(0, cursorPosition);
    tempElement.innerHTML =
      textBeforeCursor.replace(/\n/g, "<br>") + '<span id="cursor"></span>';

    // Add to body but position near the textarea for more accurate measurements
    document.body.appendChild(tempElement);
    tempElement.style.top = `${textareaRect.top}px`;
    tempElement.style.left = `${textareaRect.left}px`;

    const cursorSpan = document.getElementById("cursor");

    if (!cursorSpan) {
      document.body.removeChild(tempElement);
      return;
    }

    // Get cursor coordinates
    const cursorRect = cursorSpan.getBoundingClientRect();

    // Clean up
    document.body.removeChild(tempElement);

    // Calculate position relative to textarea
    let top = cursorRect.top - textareaRect.top + textarea.scrollTop;
    let left = cursorRect.left - textareaRect.left + textarea.scrollLeft;

    // Account for line height to position below the current line
    const lineHeight =
      Number.parseFloat(textareaStyles.lineHeight) ||
      Number.parseFloat(textareaStyles.fontSize) * 1.2;
    top += lineHeight;

    // Apply padding adjustments
    const paddingTop = Number.parseFloat(textareaStyles.paddingTop) || 0;
    const paddingLeft = Number.parseFloat(textareaStyles.paddingLeft) || 0;
    top += paddingTop;
    left += paddingLeft;

    // Ensure the dropdown stays within the container bounds
    const dropdownWidth = 256; // w-64 = 16rem = 256px
    const dropdownHeight = 208; // max-h-52 = 13rem = 208px

    // Check right boundary - don't let it extend past container width
    const rightEdge = left + dropdownWidth;
    const containerWidth = containerRect.width;
    if (rightEdge > containerWidth - 20) {
      // 20px buffer
      left = Math.max(10, containerWidth - dropdownWidth - 20);
    }

    // Check bottom boundary
    const bottomEdge = top + dropdownHeight;
    const containerHeight = containerRect.height;
    if (bottomEdge > containerHeight - 20) {
      // Position above the cursor if not enough space below
      top = Math.max(10, top - dropdownHeight - lineHeight);
    }

    // Apply scale factor for zoomed views
    const scaleFactor = 1 / (window.visualViewport?.scale || 1);
    if (scaleFactor !== 1) {
      // Adjust for browser zoom
      top = top * scaleFactor;
      left = left * scaleFactor;
    }

    setMentionPosition({
      top,
      left,
      // Store additional context for responsive positioning
      containerWidth: containerWidth,
      textareaWidth: textareaRect.width,
    });
  };

  // Handle selection from mention guide
  const handleMentionSelect = (option) => {
    // Get text before and after the current mention
    const textBeforeCursor = content.substring(0, cursorPosition);
    const textAfterCursor = content.substring(cursorPosition);

    // Find the word with @ or # or + that we need to remove
    const words = textBeforeCursor.split(/\s/);
    const currentWordIndex = words.length - 1;
    const currentWord = words[words.length - 1];

    // Remove the current mention word completely
    words[currentWordIndex] = "";

    // Join the text back together, trimming any extra spaces
    const newTextBefore = words.join(" ").replace(/\s+/g, " ").trim();
    const newText =
      newTextBefore + (newTextBefore ? " " : "") + textAfterCursor.trimStart();

    // Update the textarea content
    setContent(newText);
    setShowMentionGuide(false);

    // Update selected items based on mention type
    if (option.type === "group") {
      // Find the full group object
      const group = groups.find(
        (g) => g.groupid && g.groupid.toString() === option.id
      );
      if (group) {
        // Only allow one group selection
        setSelectedGroups([group]);

        // If the group is selected, immediately set scoop to Gally Talks
        if (scoops.length > 0) {
          const gallyTalksScoop = scoops.find(
            (scoop) => scoop.scoopname === "Gally Talks"
          );
          if (gallyTalksScoop) {
            setSelectedCategory({
              id: gallyTalksScoop.scoopid.toString(),
              name: gallyTalksScoop.scoopname,
              iconPath: `/scoops/${gallyTalksScoop.scoopname.toLowerCase()}.png`,
            });

            // Clear existing hoops when changing scoop
            setSelectedHashtags([]);

            // Set default hoop if available
            if (defaultGallyHoop) {
              setSelectedHashtags([defaultGallyHoop]);
            }
          } else {
            // Show error if Gally Talks scoop is not found
            errorToast("Gally Talks scoop not found. Please contact support.", {
              position: "top-right",
              autoClose: 5000,
            });
          }
        }
      }
    } else if (option.type === "scoop") {
      // Find the full scoop object
      const scoop = scoops.find((s) => s.scoopid.toString() === option.id);
      if (scoop) {
        // Clear existing hoops when changing scoop
        setSelectedHashtags([]);
        setSelectedCategory({
          id: scoop.scoopid.toString(),
          name: scoop.scoopname,
          iconPath: `/scoops/${scoop.scoopname.toLowerCase()}.png`,
        });

        // Set default hoop for Gally Talks if applicable
        if (
          (scoop.scoopname === "Gally Talks" ||
            scoop.scoopname === "Galli Talks") &&
          defaultGallyHoop
        ) {
          setSelectedHashtags([defaultGallyHoop]);
        }
      }
    } else if (option.type === "hoop") {
      // Find the full hoop object
      const hoop = hoops.find((h) => h.hoopid.toString() === option.id);
      if (hoop && !selectedHashtags.some((h) => h.hoopid === hoop.hoopid)) {
        setSelectedHashtags([...selectedHashtags, hoop]);
      }
    }

    // Set focus back to textarea
    if (textareaRef.current) {
      textareaRef.current.focus();

      // Set cursor position after where the mention was
      const newCursorPosition = newTextBefore.length;
      setTimeout(() => {
        textareaRef.current.selectionStart = newCursorPosition;
        textareaRef.current.selectionEnd = newCursorPosition;
      }, 0);
    }
  };

  const toggleHashtag = (hoop) => {
    if (selectedHashtags.some((h) => h.hoopid === hoop.hoopid)) {
      setSelectedHashtags(
        selectedHashtags.filter((h) => h.hoopid !== hoop.hoopid)
      );
    } else {
      setSelectedHashtags([...selectedHashtags, hoop]);
    }
  };

  // Modify the toggleGroup function to auto-close the group popover when a group is selected
  const toggleGroup = (group) => {
    if (selectedGroups.some((g) => g.groupid === group.groupid)) {
      setSelectedGroups(
        selectedGroups.filter((g) => g.groupid !== group.groupid)
      );
      // If we're removing the group, we don't need to auto-set the scoop
    } else {
      // Only allow one group selection
      setSelectedGroups([group]);

      // If a group is selected, immediately set scoop to Gally Talks
      if (scoops.length > 0) {
        const gallyTalksScoop = scoops.find(
          (scoop) =>
            scoop.scoopname === "Gally Talks" ||
            scoop.scoopname === "Galli Talks"
        );
        if (gallyTalksScoop) {
          setSelectedCategory({
            id: gallyTalksScoop.scoopid.toString(),
            name: gallyTalksScoop.scoopname,
            iconPath: `/scoops/${gallyTalksScoop.scoopname
              .toLowerCase()
              .replace(/\s+/g, "")}.png`,
          });

          // Clear existing hoops when changing scoop
          setSelectedHashtags([]);

          // Fetch hoops for this scoop
          const scoopHoops = gallyTalksScoop.hoops || [];
          const filteredHoops = hoops.filter((h) =>
            scoopHoops.some((sh) => sh.hoopid === h.hoopid)
          );
          const groupedHoops = groupHoopsByCategory(filteredHoops);
          setAvailableHoops(groupedHoops);

          // Set default hoop if available
          if (defaultGallyHoop) {
            setSelectedHashtags([defaultGallyHoop]);
          }
        } else {
          // Show error if Gally Talks scoop is not found
          errorToast("Gally Talks scoop not found. Please contact support.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      }

      // Auto-close the group popover when a group is selected
      setIsGroupPopoverOpen(false);
    }
  };

  const openImagePicker = (mode) => {
    setImagePickerMode(mode);
    setIsImagePickerOpen(true);
  };

  // Handle multiple image selection
  const handleImageSelected = (imageData) => {
    if (Array.isArray(imageData)) {
      // If we receive an array of images, limit to max 2 images total
      const newImages = [...images];
      for (const img of imageData) {
        if (newImages.length < 2) {
          newImages.push(img);
        } else {
          warningToast("Maximum 2 images allowed", {
            position: "top-right",
            autoClose: 3000,
          });
          break;
        }
      }
      setImages(newImages);
    } else if (imageData) {
      // If we receive a single image
      if (images.length < 2) {
        setImages([...images, imageData]);
      } else {
        warningToast("Maximum 2 images allowed", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  };

  // Remove a specific image
  const removeImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  useEffect(() => {
    const loadSavedData = async () => {
      try {
        setIsLoading(true);

        // Load content (simple string)
        const savedContent = localStorage.getItem("content");
        if (savedContent) {
          localStorage.removeItem("content");
        }

        // Load selected category (JSON object)
        const savedCategory = localStorage.getItem("selectedCategory");
        const parsedCategory = JSON.parse(savedCategory);
        if (savedCategory) {
          localStorage.removeItem("selectedCategory");
        }

        // Load selected hashtags (JSON array)
        const savedHashtags = localStorage.getItem("selectedHashtags");
        const parsedHashtags = JSON.parse(savedHashtags);
        if (savedHashtags) {
          localStorage.removeItem("selectedHashtags");
        }

        // Load images (base64 string -> File objects)
        const savedImages = localStorage.getItem("images");

        const imageFiles = await base64ToFiles(savedImages);

        if (savedImages) {
          localStorage.removeItem("images");
        }
        const formData = new FormData();
        if (!savedContent || !parsedCategory || !parsedHashtags) {
          return;
        }

        formData.append("contentinfo", savedContent);
        formData.append("scoopid", parsedCategory.id);
        formData.append("geohash", geohash);
        formData.append(
          "hoopids",
          parsedHashtags.map((h) => h.hoopid).join(",")
        );
        imageFiles.forEach((imageFile, index) => {
          if (index < 2) {
            formData.append("photopath", imageFile);
          }
        });
        const response = await postScoops(formData);
        //
        successToast(`Scoop posted to ${parsedCategory.name} successfully!`);
        refreshFeed();
      } catch (error) {
        console.error("Error loading saved data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedData();
  }, []);

  const handlePost = async () => {
    if (!isAuthenticated()) {
      localStorage.setItem("target", window.location.pathname);
      localStorage.setItem(
        "selectedCategory",
        JSON.stringify(selectedCategory)
      );
      localStorage.setItem(
        "selectedHashtags",
        JSON.stringify(selectedHashtags)
      );
      localStorage.setItem("content", content);
      localStorage.setItem("images", await filesToBase64(images));
      navigate("/signin");
      return;
    }

    // Helper function for error toasts
    const showErrorToast = (message) => {
      errorToast(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    };

    // Helper function to add images to FormData
    const addImagesToFormData = (formData) => {
      images.forEach((imageFile, index) => {
        if (index < 2) {
          formData.append("photopath", imageFile);
        }
      });
    };

    try {
      setIsProcessing(true);

      // Validation checks
      if (!content.trim()) {
        showErrorToast("Please enter some content");
        return;
      }

      if (!selectedCategory) {
        showErrorToast("Please select a category");
        return;
      }

      let response;
      const isGroupPost = selectedGroups.length > 0;

      if (isGroupPost) {
        // Create group post
        const groupId = selectedGroups[0].groupid;
        const formData = new FormData();

        formData.append("content", content);
        formData.append("scoopId", selectedCategory.id);
        formData.append("geoHash", geohash);
        formData.append(
          "hoopIds",
          selectedHashtags.map((h) => h.hoopid).join(",")
        );

        addImagesToFormData(formData);
        response = await handleAddGroupPost(formData, groupId);
      } else {
        // Create regular post
        const formData = new FormData();

        formData.append("contentinfo", content);
        formData.append("scoopid", selectedCategory.id);
        formData.append("geohash", geohash);
        formData.append(
          "hoopids",
          selectedHashtags.map((h) => h.hoopid).join(",")
        );

        addImagesToFormData(formData);
        response = await postScoops(formData);
      }

      // Show appropriate success message
      const successMessage = isGroupPost
        ? `Post successfully added to ${selectedGroups[0].name} group!`
        : `Scoop posted to ${selectedCategory.name} successfully!`;

      successToast(successMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });

      // Reset form
      setContent("");
      setSelectedCategory(null);
      setSelectedHashtags([]);
      setSelectedGroups([]);
      setImages([]);

      // Refresh the feed
      refreshFeed();
    } catch (error) {
      console.error("Post error:", error);

      // Extract error message from various possible formats
      const errorMessage =
        error.response?.data?.message ||
        error.data?.message ||
        error.message ||
        "An error occurred";

      errorToast(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDoneHashtags = () => {
    setIsHashtagPopoverOpen(false);
  };

  const handleDoneGroups = () => {
    setIsGroupPopoverOpen(false);
  };

  // Add location handler
  const handleAddLocation = () => {
    // In a real app, you would open a location picker
    // For now, we'll just set the geohash
    setGeohash("9v6m");
    successToast("Location added", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  // Convert API data to category items for display
  const getCategoryItems = () => {
    if (!scoops || scoops.length === 0) {
      return [];
    }

    // If a group is selected, only show Gally Talks
    if (selectedGroups.length > 0) {
      return scoops
        .filter(
          (scoop) =>
            scoop.scoopname === "Gally Talks" ||
            scoop.scoopname === "Galli Talks"
        )
        .map((scoop) => ({
          id: scoop.scoopid.toString(),
          name: scoop.scoopname,
          iconPath: `/scoops/${scoop.scoopname
            .toLowerCase()
            .replace(/\s+/g, "")}.png`,
          description: `${scoop.scoopname} scoop`,
        }));
    }

    // Otherwise show all scoops
    return scoops.map((scoop) => ({
      id: scoop.scoopid.toString(),
      name: scoop.scoopname,
      iconPath: `/scoops/${scoop.scoopname
        .toLowerCase()
        .replace(/\s+/g, "")}.png`,
      description: `${scoop.scoopname} scoop`,
    }));
  };

  const CategoryItem = ({ category, onClick }) => (
    <div
      className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
      onClick={() => {
        onClick(category);
      }}
    >
      <div className="w-10 h-10 flex items-center justify-center">
        <span style={{ fontSize: "1.5rem" }}>
          <img
            src={category.iconPath || "/placeholder.svg"}
            alt={`${category.name} icon`}
            width={20}
            height={20}
            className="object-contain"
          />
        </span>
      </div>
      <div>
        <div className="font-medium">{category.name}</div>
        <div className="text-sm text-gray-500">{category.description}</div>
      </div>
    </div>
  );

  // Modify the GroupItem component to accept a wordLimit prop with a default value
  const GroupItem = ({ group, onClick, wordLimit = 3 }) => {
    // Truncate description = (description) => {
    const truncateDescription = (description) => {
      if (!description) return "";
      const words = description.split(" ");
      if (words.length <= wordLimit) return description;
      return words.slice(0, wordLimit).join(" ") + "...";
    };

    return (
      <div
        className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200"
        onClick={() => {
          onClick(group);
        }}
      >
        <div className="w-10 h-10 flex items-center justify-center">
          <span style={{ fontSize: "1.5rem" }}>
            <Plus size={20} />
          </span>
        </div>
        <div>
          <div className="font-medium">{group.name}</div>
          <div className="text-sm text-gray-500">
            {truncateDescription(group.description)}
          </div>
        </div>
      </div>
    );
  };

  // Update the MentionItem component to be more interactive and fun
  const MentionItem = ({ option, onClick, isSelected }) => {
    let icon;
    let bgColor;

    if (option.type === "scoop") {
      icon = (
        <img
          src={option.iconPath || "/placeholder.svg"}
          alt={option.name}
          width={16}
          height={16}
          className="object-contain"
        />
      );
      bgColor = "bg-amber-100";
    } else if (option.type === "hoop") {
      icon = <Hash size={14} />;
      bgColor = "bg-fuchsia-100";
    } else if (option.type === "group") {
      icon = <Plus size={14} />;
      bgColor = "bg-green-100";
    }

    // Highlight matching text
    const highlightedName = () => {
      if (!mentionText) return option.name;

      const index = option.name
        .toLowerCase()
        .indexOf(mentionText.toLowerCase());
      if (index === -1) return option.name;

      const before = option.name.substring(0, index);
      const match = option.name.substring(index, index + mentionText.length);
      const after = option.name.substring(index + mentionText.length);

      return (
        <>
          {before}
          <span className="font-bold bg-yellow-100">{match}</span>
          {after}
        </>
      );
    };

    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-gray-200 ${
          isSelected ? "bg-gray-100" : "hover:bg-gray-100"
        }`}
        onClick={() => {
          onClick(option);
        }}
      >
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center ${bgColor}`}
        >
          {icon}
        </div>
        <div className="font-medium">{highlightedName()}</div>
      </div>
    );
  };
  const categoryPopoverContent = (
    <div
      className="flex flex-col w-fit max-h-[400px] overflow-y-auto popover-content"
      ref={categoryPopoverRef}
    >
      {isLoading ? (
        <div className="p-4 text-center">Loading categories...</div>
      ) : (
        getCategoryItems().map((category) => (
          <CategoryItem
            key={category.id}
            category={category}
            onClick={(category) => {
              setSelectedHashtags([]); // clear hoops on category change
              setSelectedCategory(category);
              setIsCategoryPopoverOpen(false);

              if (
                (category.name === "Gally Talks" ||
                  category.name === "Galli Talks") &&
                defaultGallyHoop
              ) {
                setSelectedHashtags([defaultGallyHoop]);
              }
            }}
          />
        ))
      )}
    </div>
  );

  // Update the groupPopoverContent to pass a wordLimit prop to GroupItem
  const groupPopoverContent = (
    <div
      className="flex flex-col w-fit max-h-[400px] overflow-y-auto popover-content"
      ref={groupPopoverRef}
    >
      {isLoading ? (
        <div className="p-4 text-center">Loading groups...</div>
      ) : groups.length === 0 ? (
        <div className="p-4 text-center">Nothing to show</div>
      ) : (
        <>
          {groups.map((group) => (
            <GroupItem
              key={group.groupid}
              group={group}
              onClick={(group) => toggleGroup(group)}
              wordLimit={3}
            />
          ))}
          <div className="p-4 flex justify-end">
            <Button
              className="ml-auto flex items-center gap-1 text-black text-sm rounded-lg bg-[#7B189F] hover:bg-[#8e23b5] hover:text-white"
              onClick={handleDoneGroups}
            >
              <Check size={16} />
              <span>Done</span>
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const hashtagPopoverContent = (
    <div
      className="p-4 flex flex-col gap-4 max-h-[400px] overflow-y-auto popover-content"
      ref={hashtagPopoverRef}
    >
      {isLoading ? (
        <div className="text-center">Loading hashtags...</div>
      ) : !selectedCategory ? (
        <div className="text-center">Please select a category first</div>
      ) : availableHoops.length === 0 ? (
        <div className="text-center">No hoops available for this category</div>
      ) : (
        <>
          {availableHoops.map((group, index) => (
            <div key={index} className="flex flex-wrap gap-2">
              {group.map((hoop) => (
                <Hashtag
                  key={hoop.hoopid}
                  tag={hoop.hooptag}
                  selected={selectedHashtags.some(
                    (h) => h.hoopid === hoop.hoopid
                  )}
                  onClick={() => toggleHashtag(hoop)}
                />
              ))}
            </div>
          ))}
          <Button
            className="ml-auto flex items-center gap-1 text-black text-sm rounded-lg bg-[#7B189F] hover:bg-[#8e23b5] hover:text-white"
            onClick={handleDoneHashtags}
          >
            <Check size={16} />
            <span>Done</span>
          </Button>
        </>
      )}
    </div>
  );

  // Handle keyboard navigation in mention guide
  const handleKeyDown = (e) => {
    if (showMentionGuide && mentionOptions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex((prevIndex) =>
          prevIndex < mentionOptions.length - 1 ? prevIndex + 1 : prevIndex
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : 0
        );
      } else if (e.key === "Enter" && mentionOptions.length > 0) {
        e.preventDefault();
        handleMentionSelect(mentionOptions[selectedMentionIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionGuide(false);
      }
    }
  };

  // Recalculate position when content changes
  useEffect(() => {
    if (showMentionGuide) {
      calculateMentionPosition();
    }
  }, [content, showMentionGuide]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (showMentionGuide) {
        calculateMentionPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [showMentionGuide]);

  // Handle textarea scroll events
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const handleScroll = () => {
      if (showMentionGuide) {
        calculateMentionPosition();
      }
    };

    textarea.addEventListener("scroll", handleScroll);
    return () => {
      textarea.removeEventListener("scroll", handleScroll);
    };
  }, [showMentionGuide, textareaRef.current]);

  // Add click outside handler to close mention guide
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mentionGuideRef.current &&
        !mentionGuideRef.current.contains(event.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target)
      ) {
        setShowMentionGuide(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Add CSS for mention guide animations
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .mention-guide-animation {
        animation: fadeIn 0.2s ease-out;
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const handleGlobalPointerDown = (e) => {
      const target = e.target;

      // If click is inside ANY popover content (even when portaled), do nothing
      const insideAnyPopoverContent = !!target.closest(".popover-content");
      if (insideAnyPopoverContent) return;

      // If click is on ANY trigger, do nothing
      const onAnyTrigger =
        groupTriggerRef.current?.contains(target) ||
        categoryTriggerRef.current?.contains(target) ||
        hashtagTriggerRef.current?.contains(target);

      if (onAnyTrigger) return;

      // Otherwise close all that are open
      if (isGroupPopoverOpen) setIsGroupPopoverOpen(false);
      if (isCategoryPopoverOpen) setIsCategoryPopoverOpen(false);
      if (isHashtagPopoverOpen) setIsHashtagPopoverOpen(false);
    };

    // capture=true so we run early & across portals reliably
    document.addEventListener("pointerdown", handleGlobalPointerDown, true);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handleGlobalPointerDown,
        true
      );
    };
  }, [isGroupPopoverOpen, isCategoryPopoverOpen, isHashtagPopoverOpen]);

  return (
    <>
      <div className="bg-white rounded-lg border p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {/* Reordered buttons: Group first (only shown if logged in), then Scoop, then Hoop */}
          {userIsLoggedIn && groups.length > 0 && (
            <Popover
              content={groupPopoverContent}
              align="start"
              open={isGroupPopoverOpen}
              onOpenChange={(open) => {
                setIsGroupPopoverOpen(open);
                if (open) fetchGroups();
              }}
            >
              {/* If Button DOES NOT forward ref: */}
              <span ref={groupTriggerRef}>
                <Button
                  variant="outline"
                  className="rounded-full flex items-center gap-2 bg-green-100 text-gray-700"
                  onClick={() => {
                    fetchGroups();
                    setIsGroupPopoverOpen(true);
                  }}
                >
                  <div className="w-6 h-6 rounded-full bg-green-300 text-green-800 flex items-center justify-center">
                    <Plus size={16} />
                  </div>
                  <span>
                    {selectedGroups.length > 0
                      ? selectedGroups[0].name
                      : "Select a group (optional)"}
                  </span>
                  <ChevronDown size={16} />
                </Button>
              </span>

              {/*
    If Button forwards ref, you can instead do:
    <Button ref={groupTriggerRef} ...>...</Button>
  */}
            </Popover>
          )}

          <Popover
            content={categoryPopoverContent}
            align="start"
            open={isCategoryPopoverOpen}
            onOpenChange={(open) => {
              if (selectedGroups.length === 0) setIsCategoryPopoverOpen(open);
            }}
          >
            <span ref={categoryTriggerRef}>
              <Button
                variant="outline"
                className={`rounded-full flex items-center gap-2 bg-amber-100 text-gray-700 ${
                  selectedGroups.length > 0
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
                disabled={selectedGroups.length > 0}
              >
                <div className="w-6 h-6 rounded-full bg-amber-300 text-amber-800 flex items-center justify-center">
                  <img
                    src="/images/scoops_icon.svg"
                    alt="scoops icon"
                    width={16}
                    height={16}
                    className="object-contain"
                  />
                </div>
                <span>
                  {selectedCategory ? selectedCategory.name : "Select a scoop"}
                </span>
                <ChevronDown
                  size={16}
                  className={selectedGroups.length > 0 ? "opacity-50" : ""}
                />
              </Button>
            </span>
          </Popover>

          <Popover
            content={hashtagPopoverContent}
            align="start"
            open={isHashtagPopoverOpen}
            onOpenChange={(open) => {
              if (open && !selectedCategory) {
                warningToast("Please select a scoop first", {
                  position: "top-right",
                  autoClose: 3000,
                });
                return;
              }
              setIsHashtagPopoverOpen(open);
            }}
          >
            <span ref={hashtagTriggerRef}>
              <Button
                variant="outline"
                className={`rounded-full flex items-center gap-2 text-gray-700 bg-fuchsia-100 ${
                  !selectedCategory ? "opacity-70 cursor-not-allowed" : ""
                }`}
                disabled={!selectedCategory}
              >
                <div className="w-6 h-6 rounded-full bg-fuchsia-300 text-fuchsia-800 flex items-center justify-center">
                  <Hash size={14} />
                </div>
                <span>
                  {selectedHashtags.length > 0
                    ? selectedHashtags[selectedHashtags.length - 1].hooptag
                    : "Add Hoop"}
                  {selectedHashtags.length > 1 &&
                    ` +${selectedHashtags.length - 1}`}
                </span>
                <ChevronDown
                  size={16}
                  className={!selectedCategory ? "opacity-50" : ""}
                />
              </Button>
            </span>
          </Popover>
        </div>

        <div className="mb-4 relative">
          <textarea
            ref={textareaRef}
            className="w-full p-2 min-h-[100px] border-none outline-none resize-none"
            placeholder="What's on your mind? Pick a + Group or start a @ Scoop, add the # Hoop — your voice, your vibe"
            value={content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
          />

          {showMentionGuide && (
            <div
              ref={mentionGuideRef}
              className="bg-white border rounded-md shadow-lg overflow-y-auto max-h-52 w-64 mention-guide-animation"
              style={{
                position: "relative", // enables z-index without removing from flow
                zIndex: 99999,
                maxWidth: "calc(100% - 1rem)",
                marginTop: "-0.75rem",
                marginLeft: mentionPosition.left,
                display: showMentionGuide ? "block" : "none",
              }}
            >
              <div className="sticky top-0 p-2 bg-gray-100 border-b flex items-center gap-2 z-20">
                <Search size={14} className="text-gray-600" />
                <span className="text-sm font-medium">
                  {mentionType === "+"
                    ? `👥 Select a group${
                        mentionText ? `: ${mentionText}` : ""
                      }`
                    : mentionType === "@"
                    ? `🔍 Select a scoop${
                        mentionText ? `: ${mentionText}` : ""
                      }`
                    : `🏷️ Select a hoop${
                        mentionText ? `: ${mentionText}` : ""
                      }`}
                </span>
              </div>

              {mentionOptions.length > 0 ? (
                <div className="py-1">
                  {mentionOptions.map((option, index) => (
                    <MentionItem
                      key={`${option.type}-${option.id}`}
                      option={option}
                      onClick={handleMentionSelect}
                      isSelected={index === selectedMentionIndex}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-3 text-center text-gray-500 italic">
                  No matches found
                  <div className="text-xl mt-1">🔍</div>
                </div>
              )}

              <div className="p-2 bg-gray-50 border-t text-xs text-gray-500 flex justify-between">
                <span>↑↓ to navigate</span>
                <span>↵ to select</span>
                <span>Esc to close</span>
              </div>
            </div>
          )}
        </div>

        {/* Display multiple images */}
        {images.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-2">
            {images.map((img, index) => (
              <div key={index} className="relative">
                <img
                  src={img instanceof File ? URL.createObjectURL(img) : img}
                  alt={`Uploaded content ${index + 1}`}
                  className="w-full h-auto rounded-md object-cover aspect-square"
                />
                <button
                  className="absolute top-2 right-2 bg-white/80 rounded-full p-1 hover:bg-white"
                  onClick={() => removeImage(index)}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Selected items pills section - reordered to match the new flow */}
        {(selectedGroups.length > 0 ||
          selectedCategory ||
          selectedHashtags.length > 0) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {/* Group pill - always first */}
            {selectedGroups.map((group) => (
              <span
                key={group.groupid}
                className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-gray-700 flex items-center gap-1"
                onClick={() => toggleGroup(group)}
              >
                {group.name}
                <X
                  size={14}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedGroups([]);
                  }}
                />
              </span>
            ))}

            {/* Scoop pill - always second */}
            {selectedCategory && (
              <span
                className="px-3 py-1 rounded-full text-sm font-medium bg-black text-white flex items-center gap-1"
                onClick={() => {
                  // Only allow clicking if no group is selected
                  if (selectedGroups.length === 0) {
                    setIsCategoryPopoverOpen(true);
                  }
                }}
              >
                {selectedCategory.name}
                <X
                  size={14}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Only allow removing if no group is selected
                    if (selectedGroups.length === 0) {
                      setSelectedCategory(null);
                      setSelectedHashtags([]); // Clear all selected hoops when scoop is unselected
                    }
                  }}
                />
              </span>
            )}

            {/* Hoops pills - always third */}
            {selectedHashtags.map((hoop) => (
              <span
                key={hoop.hoopid}
                className="px-3 py-1 rounded-full text-xs font-medium bg-fuchsia-100 text-gray-700 flex items-center gap-1"
                onClick={() => toggleHashtag(hoop)}
              >
                {hoop.hooptag}
                <X size={12} className="cursor-pointer" />
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => openImagePicker("camera")}
              className="text-gray-500 hover:text-gray-800 transition-colors"
            >
              <Camera size={20} />
            </button>
            <button
              onClick={() => openImagePicker("upload")}
              className="text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ImageIcon size={20} />
            </button>
          </div>

          <Button
            variant="primary"
            className="rounded-full px-6 bg-[#7B189F] text-white hover:bg-[#8823ad] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handlePost}
            disabled={
              isProcessing ||
              (!content.trim() && images.length === 0) ||
              !selectedCategory ||
              selectedHashtags.length === 0 // ⬅️ new: require ≥1 hoop
            }
          >
            {isProcessing ? "Posting..." : "Post"}
          </Button>
        </div>

        <ImagePickerModal
          isOpen={isImagePickerOpen}
          onClose={() => setIsImagePickerOpen(false)}
          onImageSelected={handleImageSelected}
          mode={imagePickerMode}
          allowMultiple={true}
        />
      </div>

      {/* Social Feed Section */}
      <div className="social-feed">
        <div className="flex pt-4 flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 ">
          <h1 className="text-xl lg:text-2xl font-bold font-fraunces">
            Scoops Around You
          </h1>
          <div className="flex items-center gap-3">
            <Link
              to="/groups"
              className="px-3 py-1.5 bg-[#7B189F] text-white rounded-md font-medium shadow-lg hover:bg-[#8F1DB8] transition-all duration-200 transform hover:scale-105 flex items-center"
            >
              Browse Groups
            </Link>
          </div>
        </div>

        {/* Initial loading state with skeletons */}
        {initialLoading && (
          <div className="space-y-4">
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
              <span className="ml-2 text-gray-500">Loading posts...</span>
            </div>
            {renderPostSkeletons(3)}
          </div>
        )}

        {/* Error state */}
        {error && !initialLoading && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            <p>Failed to load posts: {error}</p>
            <button
              className="mt-2 text-sm font-medium underline"
              onClick={refreshFeed}
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!initialLoading && !error && postings.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-8 text-center">
            <p className="text-gray-500 mb-2">No posts found</p>
            <p className="text-sm text-gray-400">
              Be the first to create a post!
            </p>
          </div>
        )}

        {/* Posts list */}
        {!initialLoading && postings.length > 0 && (
          <div className="space-y-4">
            {postings.map((post, index) => (
              <div
                key={post.id || post._id || index}
                ref={index === postings.length - 1 ? lastPostElementRef : null}
                className="transition-all duration-300 ease-in-out"
              >
                <SocialPostCard
                  followingUsers={followingUsers}
                  postings={postings}
                  setPostings={setPostings}
                  post={post}
                  username={post.username}
                  images={post.photopath ? post.photopath.split(",") : []}
                />
              </div>
            ))}

            {/* Loading more indicator */}
            {feedLoading && !initialLoading && (
              <div className="flex justify-center items-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">
                  Loading more...
                </span>
              </div>
            )}

            {/* End of feed indicator */}
            {!hasMore && !feedLoading && postings.length > 0 && (
              <div className="text-center py-6 text-gray-500 text-sm border-t border-gray-100">
                You've reached the end of the feed
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default CreateScoopForm;
